import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { FUNCTIONAL_KANBAN_MOCK } from '../mockData';
import {
  COLUMN_ACCENTS,
  PRIORITY_STYLES,
  STATUS_STYLES,
} from './constants';

const BOARD_HEIGHT = 'calc(100vh - 320px)';

const Pill = ({ label, bg, color }) => (
  <span className="initiatives-jklc__pill" style={{ background: bg, color }}>
    {label}
  </span>
);

const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.Default;

const InitiativeCard = ({ card, columnKey, isDragging, onDragStart, onDragEnd, onView }) => {
  const priorityStyle = PRIORITY_STYLES[card.priority] || PRIORITY_STYLES.Low;
  const statusStyle = getStatusStyle(card.status);
  const accentColor = COLUMN_ACCENTS[columnKey] || '#475569';

  return (
    <Box
      className="initiatives-jklc__card"
      style={{
        borderLeftColor: accentColor,
        opacity: isDragging ? 0.4 : 1,
      }}
      draggable
      onDragStart={(e) => onDragStart(e, card, columnKey)}
      onDragEnd={onDragEnd}
      sx={{ mb: 1.5 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
        <span className="initiatives-jklc__card-id">{card.id}</span>
        <Pill label={card.priority} bg={priorityStyle.bg} color={priorityStyle.color} />
      </Box>
      <div className="initiatives-jklc__card-title">{card.title}</div>
      <div className="initiatives-jklc__card-meta">
                      Owner: <span>{card.owner || '—'}</span>
                    </div>
                    <div className="initiatives-jklc__card-meta">
                      Reports To: <span>{card.reportsTo || '—'}</span>
      </div>
      <div className="initiatives-jklc__card-meta">
        Target: <span>{card.target}</span>
      </div>
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
        <Pill label={card.status} bg={statusStyle.bg} color={statusStyle.color} />
        <VisibilityOutlinedIcon
          sx={{ fontSize: 16, color: '#4A4A4A', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onView(card);
          }}
        />
      </Box>
      {card.pending && (
        <Typography
          sx={{
            fontSize: 11.5,
            color: '#9CA3AF',
            mt: 1,
            pt: 1,
            borderTop: '1px dashed #D9D9D9',
          }}
        >
          Awaiting HQ approval
        </Typography>
      )}
    </Box>
  );
};

const KanbanBoard = ({
  department,
  createOpen = false,
  onCreateClose,
}) => {
  const [columns, setColumns] = useState(FUNCTIONAL_KANBAN_MOCK);
  const [dragCard, setDragCard] = useState(null);
  const [viewCard, setViewCard] = useState(null);
  const [newCard, setNewCard] = useState({
    title: '',
    owner: '',
    reportsTo: '',
    priority: 'Medium',
    target: '',
  });

  useEffect(() => {
    setColumns(
      FUNCTIONAL_KANBAN_MOCK.map((col) => ({
        ...col,
        cards: col.cards.map((c) => ({ ...c, department })),
      }))
    );
  }, [department]);

  const handleDragStart = (e, card, columnKey) => {
    setDragCard({ card, from: columnKey });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => setDragCard(null);

  const handleDrop = (toKey) => {
    if (!dragCard || dragCard.from === toKey) {
      setDragCard(null);
      return;
    }
    setColumns((prev) =>
      prev.map((col) => {
        if (col.key === dragCard.from) {
          return { ...col, cards: col.cards.filter((c) => c.id !== dragCard.card.id) };
        }
        if (col.key === toKey) {
          return { ...col, cards: [...col.cards, dragCard.card] };
        }
        return col;
      })
    );
    setDragCard(null);
  };

  const handleCreate = () => {
    if (!newCard.title.trim()) return;
    const card = {
      id: `INIT-${String(Date.now()).slice(-6)}`,
      priority: newCard.priority || 'Medium',
      title: newCard.title,
      owner: newCard.owner || 'Owner',
      reportsTo: newCard.reportsTo || '-',
      target: newCard.target || '-',
      status: 'On Track',
      pending: false,
      department,
    };
    setColumns((prev) =>
      prev.map((col) => (col.key === 'L1' ? { ...col, cards: [card, ...col.cards] } : col))
    );
    setNewCard({ title: '', owner: '', reportsTo: '', priority: 'Medium', target: '' });
    if (onCreateClose) onCreateClose();
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          overflowX: 'auto',
          pb: 1,
          minHeight: BOARD_HEIGHT,
        }}
      >
        {columns.map((col) => (
          <Box
            key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(col.key)}
            sx={{
              flex: 1,
              minWidth: 210,
              height: BOARD_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#EBEFF6',
              border: '1px solid #E1E4EC',
              borderRadius: '10px',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 1.75,
                py: 1.25,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                borderBottom: '2px solid #C4CEE2',
                bgcolor: '#EBEFF6',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#215093' }}>
                  {col.key}
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#12203E' }}>
                  {col.label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>
                ({col.cards.length})
              </Typography>
            </Box>
            <Box sx={{ px: 1.25, pt: 1.5, pb: 1.25, flex: 1, overflowY: 'auto' }}>
              {col.cards.map((card) => (
                <InitiativeCard
                  key={card.id}
                  card={card}
                  columnKey={col.key}
                  isDragging={dragCard && dragCard.card.id === card.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onView={setViewCard}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={Boolean(viewCard)} onClose={() => setViewCard(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#215093', fontWeight: 600 }}>{viewCard?.id}</DialogTitle>
        <DialogContent dividers>
          {viewCard && (
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{viewCard.title}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Owner: {viewCard.owner}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Reports To: {viewCard.reportsTo}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Target: {viewCard.target}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Priority: {viewCard.priority}</Typography>
              <Typography variant="body2">Status: {viewCard.status}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewCard(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={onCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#215093', fontWeight: 600 }}>Create Initiative</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
            <TextField
              label="Title"
              size="small"
              fullWidth
              required
              value={newCard.title}
              onChange={(e) => setNewCard((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              label="Owner"
              size="small"
              fullWidth
              value={newCard.owner}
              onChange={(e) => setNewCard((p) => ({ ...p, owner: e.target.value }))}
            />
            <TextField
              label="Reports To"
              size="small"
              fullWidth
              value={newCard.reportsTo}
              onChange={(e) => setNewCard((p) => ({ ...p, reportsTo: e.target.value }))}
            />
            <TextField
              label="Priority"
              size="small"
              fullWidth
              select
              SelectProps={{ native: true }}
              value={newCard.priority}
              onChange={(e) => setNewCard((p) => ({ ...p, priority: e.target.value }))}
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </TextField>
            <TextField
              label="Target"
              size="small"
              fullWidth
              value={newCard.target}
              onChange={(e) => setNewCard((p) => ({ ...p, target: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCreateClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{ backgroundColor: '#215093', '&:hover': { backgroundColor: '#1a4078' } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KanbanBoard;
